import { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import { PlanogramItem, UnrangedPlanogramItem } from "@CommonTypes/merchflow/pog/item";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import React, { useRef } from "react";
import styled from "styled-components";

import { DragBounds, DragModifications, PogAtom, Scaleable } from "../../store/types";
import { resolveDraggedItems } from "../../store/utils";
import { Draggable } from "../Draggable/Draggable";
import { Dropper } from "../Item/Dropper";
import { DraggedItemsPayload } from "../Item/store/types";
import { CanvasItemLabelRule, Item } from "../Item/Item";
import { onDropOverToShelf } from "../Item/store/utils";
import { Overflowing } from "./Overflowing";
import { ShelfBottom } from "./ShelfBottom";
import { ResponseMerchFlowApplyFilters } from "@CommonApi/merchandise";
import {
  planogramHoveredShelfAtom,
  planogramSelectedShelfAtom,
  showShelfOverflowAtom,
  planogramSelectedShelvesAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import { Flex } from "src/elements";
import { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import { usePogAtomActions } from "../../store/actions";
import { calculateShelfSelectionUpdate } from "../../store/utils";
import { usePlanogramPanel } from "../Panel/store/hooks";
import { getMergedMerchandisableWidth } from "@CommonUtils/pog/shelfMerchandising";
import { NonMerchandisableArea } from "./NonMerchandisableArea";
import { ItemSlot, ShelfDividerMark, getDividedRunLayout, getItemSlotWidths } from "./itemSlots";
import { useShowDividers } from "../../common/shelfDividersView";
import { useDrawnSqueeze } from "../../common/drawnSqueeze";
import { getDividerDrawSpan, SHELF_DIVIDER_Z_INDEX } from "../../common/shelfDividerDraw";
import { DRAWN_LOADER_TRACK_HEIGHT, LOADER_TRACK_COLOR } from "../../common/shelfLoaderView";

const ShelfInner = styled(Flex)<{ height: number; scaleY: number }>`
  width: 100%;
  height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: absolute;
  bottom: 0;
  left: 0;
`;

const Items = styled(Flex)<{
  $merchandisableWidth: number;
  $scaleX: number;
  isSideView?: boolean;
  align?: "left" | "right" | "between" | "around";
}>`
  width: ${({ $merchandisableWidth, $scaleX }) => `calc(${$merchandisableWidth}px * ${$scaleX})`};
  height: 0;
  position: relative;
  align-items: flex-end;
  justify-content: ${({ isSideView, align }) => {
    if (isSideView) return "flex-end";
    if (align === "right") return "flex-end";
    if (align === "left") return "flex-start";
    if (align === "around") return "space-around";
    return "space-between";
  }};
  flex-grow: 1;

  // First element is dropper, ignore that
  // Second element is always first item in the shelf.
  > *:nth-of-type(2) {
    flex-grow: 0;
  }

  ${({ align }) =>
    (align === "left" || align === "right") &&
    `
    > * {
      flex-grow: 0;
    }
  `}
`;

const ItemsAnchor = styled(Flex)`
  position: absolute;
`;

/** Side view: the spring loader track over the unusable back band; the side view's back is its left. */
const SideViewLoaderTrack = styled.div`
  position: absolute;
  left: 0;
  bottom: 0;
  z-index: ${SHELF_DIVIDER_Z_INDEX};
  background-color: ${LOADER_TRACK_COLOR};
  pointer-events: none;
`;

const ShelfBottoms = styled(Flex)``;

const ShelfTag = styled.div`
  position: absolute;
  right: 2px;
  z-index: 5;
  pointer-events: none;
  font-size: 11px;
`;

/** Content drawn at the top-right of a merged shelf run, such as its available space. */
export type ShelfTagRule = (shelves: PlanogramShelf[]) => React.ReactNode;

interface Props extends Scaleable {
  currentStoreCode?: string;
  bay: PlanogramBay;
  shelves: PlanogramShelf[];
  bounds: DragBounds;
  isEditable: boolean;
  isPog: boolean;
  highlightedItems: string[];
  pogAtom: PogAtom;
  setIsModified: (modified: boolean) => void;
  availableStores: ResponseMerchFlowApplyFilters["filter_config"] | null;
  pdfMode: boolean;
  customItemLabelOnly?: boolean;
  customLabelRule?: (item: PlanogramItem | UnrangedPlanogramItem) => string | null | undefined;
  canvasItemLabelRule?: CanvasItemLabelRule;
  shelfTagRule?: ShelfTagRule;
  pdfShelfLabelBayNo?: number;
  isSideView?: boolean;
  /** Side view only: the original shelf's spring loader depth, drawn as a dark band at the back (left). */
  sideViewLoaderDepth?: number;
  bayShift?: number;
}

export const MergedShelf = ({
  currentStoreCode,
  bay,
  shelves,
  bounds,
  isEditable,
  isPog,
  highlightedItems,
  scaleX,
  scaleY,
  pogAtom,
  setIsModified,
  availableStores,
  pdfMode,
  customItemLabelOnly,
  customLabelRule,
  canvasItemLabelRule,
  shelfTagRule,
  pdfShelfLabelBayNo,
  isSideView,
  sideViewLoaderDepth = 0,
  bayShift = 0,
}: Props) => {
  const pog = useAtomValue(pogAtom);
  const { addItemsToShelf, duplicateItemsToShelf, moveShelf } = usePogAtomActions(pogAtom);

  const setSelectedShelf = useSetAtom(planogramSelectedShelfAtom);
  const [selectedShelfIds, setSelectedShelfIds] = useAtom(planogramSelectedShelvesAtom);
  const { setTab } = usePlanogramPanel();
  const hoveredShelf = useAtomValue(planogramHoveredShelfAtom);
  const showShelfOverflow = useAtomValue(showShelfOverflowAtom);

  const firstShelf = shelves[0];
  const lastShelf = shelves[shelves.length - 1];
  const shelfHeight = firstShelf.getHeight();
  const shelfThickness = firstShelf.thickness;
  const merchandisableWidth = getMergedMerchandisableWidth(shelves);
  const isSelected = shelves.some((shelf) => selectedShelfIds.includes(shelf.uniqueId));

  const items: PlanogramItem[] = shelves.reduce(
    (items, shelf) => [...items, ...shelf.items],
    [] as PlanogramItem[],
  );
  const { showDividers } = useShowDividers(pog);
  // Dividers and obstructions give each item a fixed slot: dividers stand between the products at
  // the model's offsets, and no product lands on a strip.
  const drawn = useDrawnSqueeze();
  const { dividedRun, itemSlotWidths } = drawn(items, () => {
    if (isSideView) return { dividedRun: null, itemSlotWidths: null };
    const run = getDividedRunLayout(shelves, items);
    return {
      dividedRun: run,
      itemSlotWidths: run?.slotWidths ?? getItemSlotWidths(shelves, items),
    };
  });
  const itemsWidth = itemSlotWidths
    ? shelves.reduce((total, shelf) => total + shelf.width, 0)
    : merchandisableWidth;

  const refItems = useRef<HTMLDivElement>(null);

  const onModify = ({ yBottom }: DragModifications) => {
    setIsModified(true);
    moveShelf(firstShelf, firstShelf.getNewNotchNo(yBottom));
  };

  const onDropOver = () => {
    setIsModified(true);

    onDropOverToShelf({
      refItems,
      onDrop,
    });
  };

  const onDrop = ({ itemIds, mode }: DraggedItemsPayload) => {
    setIsModified(true);

    if (mode === "copy") {
      duplicateItemsToShelf(itemIds, firstShelf);
      return;
    }

    if (!pog) {
      return;
    }

    const draggedItems = resolveDraggedItems(pog, itemIds);

    if (draggedItems.length > 0) {
      addItemsToShelf(draggedItems, firstShelf);
    }
  };

  const setSelected = (event: React.PointerEvent) => {
    const shelf = hoveredShelf || firstShelf;
    const { nextSelectedShelfIds, nextSelectedShelf, nextTab } = calculateShelfSelectionUpdate(
      pog,
      selectedShelfIds,
      shelf,
      event,
    );

    setSelectedShelfIds(nextSelectedShelfIds);
    setSelectedShelf(nextSelectedShelf);
    setTab(nextTab);
  };

  return (
    <Draggable
      uniqueId={firstShelf.uniqueId}
      xLeft={(isSideView ? bounds.xLeft : firstShelf.getXLeft()) + bayShift}
      xRight={(isSideView ? bounds.xRight : lastShelf.getXRight()) + bayShift}
      yBottom={isSideView ? bounds.yBottom : firstShelf.getYBottom()}
      yTop={isSideView ? bounds.yTop : firstShelf.getYBottom()}
      scaleX={scaleX}
      scaleY={scaleY}
      bounds={{
        ...bounds,
        xLeft: bounds.xLeft + bayShift,
        xRight: bounds.xRight + bayShift,
      }}
      snappables={[]}
      isDraggable={isEditable}
      direction="vertical"
      moveBy={bay.notchSpacing}
      isSideView={isSideView}
      onModify={onModify}
      onClick={setSelected}
    >
      <ShelfInner height={shelfHeight} scaleY={scaleY}>
        {isPog && items.length <= 1 && (
          <Dropper
            side="left"
            width={`${merchandisableWidth * scaleX}px`}
            height={`calc(${shelfHeight - shelfThickness}px * ${scaleY})`}
            bottom={`calc(${shelfThickness}px * ${scaleY})`}
            onDropOver={onDropOver}
            onDrop={onDrop}
          />
        )}

        {!pdfMode && showShelfOverflow && firstShelf.isOverflowing() && (
          <Overflowing shelf={firstShelf} scaleY={scaleY} />
        )}

        {!pdfMode && !isSideView && shelfTagRule && (
          <ShelfTag style={{ bottom: `calc(${shelfHeight}px * ${scaleY} - 20px)` }}>
            {shelfTagRule(shelves)}
          </ShelfTag>
        )}

        <Items
          ref={refItems}
          $merchandisableWidth={isSideView ? bounds.xRight - bounds.xLeft : itemsWidth}
          $scaleX={scaleX}
          isSideView={isSideView}
          align={itemSlotWidths ? "left" : firstShelf.align}
        >
          <ItemsAnchor />

          {items.map((item, i) => {
            const renderedItem = (
              <Item
                currentStoreCode={currentStoreCode}
                item={item}
                scaleX={scaleX}
                scaleY={scaleY}
                isEditable={isEditable}
                isHighlighted={highlightedItems.includes(item.uniqueId)}
                isScrolledToView={
                  highlightedItems.length === 1 && highlightedItems.includes(item.uniqueId)
                }
                pogAtom={pogAtom}
                setIsModified={setIsModified}
                availableStores={availableStores}
                pdfMode={pdfMode}
                customItemLabelOnly={customItemLabelOnly}
                customLabelRule={customLabelRule}
                canvasItemLabelRule={canvasItemLabelRule}
              />
            );

            return (
              <React.Fragment key={i}>
                {itemSlotWidths ? (
                  <ItemSlot
                    $align="flex-end"
                    data-item-slot={itemSlotWidths[i]}
                    style={{
                      width: `${itemSlotWidths[i] * scaleX}px`,
                      ...(dividedRun &&
                        dividedRun.trailingWidths[i] > 0 && {
                          boxSizing: "border-box",
                          paddingRight: `${dividedRun.trailingWidths[i] * scaleX}px`,
                        }),
                    }}
                  >
                    {renderedItem}
                  </ItemSlot>
                ) : (
                  renderedItem
                )}
              </React.Fragment>
            );
          })}

          {isSideView && sideViewLoaderDepth > 0 && (
            <SideViewLoaderTrack
              data-testid="side-view-loader-track"
              style={{
                width: `${sideViewLoaderDepth * scaleX}px`,
                height: `${Math.max(3, DRAWN_LOADER_TRACK_HEIGHT * scaleY)}px`,
              }}
            />
          )}

          {showDividers &&
            dividedRun?.dividers.map((divider) => {
              const span = getDividerDrawSpan(divider.x, divider.width, scaleX);
              return (
                <ShelfDividerMark
                  key={divider.key}
                  data-testid="shelf-divider"
                  data-divider-x={divider.x}
                  style={{
                    left: `${span.x * scaleX}px`,
                    width: `${span.width * scaleX}px`,
                    height: `${dividedRun.dividerHeight * scaleY}px`,
                  }}
                />
              );
            })}
        </Items>

        <ShelfBottoms>
          {shelves.map((shelf, i) => (
            <ShelfBottom
              key={i}
              shelf={shelf}
              scaleX={scaleX}
              scaleY={scaleY}
              isSelected={isSelected}
              pogAtom={pogAtom}
              showShelfName={pdfMode}
              shelfLabelBayNo={pdfShelfLabelBayNo}
              setIsModified={setIsModified}
            />
          ))}
        </ShelfBottoms>

        {!isSideView && <NonMerchandisableArea scaleX={scaleX} scaleY={scaleY} shelves={shelves} />}
      </ShelfInner>
    </Draggable>
  );
};
